import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Activity, X, Search } from 'lucide-react';
import { api } from '../lib/api.ts';
import { type PagedResponse } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal } from '../components/ui/Modal.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { Pagination, usePagination } from '../components/ui/Pagination.tsx';

interface SignalEntry {
  id: string;
  eventType: string;
  customerId: string | null;
  source: string;
  payloadJson: Record<string, unknown> | null;
  occurredAt: string;
  processedAt: string | null;
  dedupeKey: string | null;
  schemaWarnings: string[] | null;
}

type PayloadOperator = 'eq' | 'contains' | 'gt' | 'lt' | 'exists';

interface PayloadCondition {
  field: string;
  operator: PayloadOperator;
  value: string;
}

const OPERATORS: { value: PayloadOperator; label: string }[] = [
  { value: 'eq', label: '=' },
  { value: 'contains', label: 'contains' },
  { value: 'gt', label: '>' },
  { value: 'lt', label: '<' },
  { value: 'exists', label: 'exists' },
];

function emptyCondition(): PayloadCondition {
  return { field: '', operator: 'eq', value: '' };
}

function formatTs(d: string) {
  return new Date(d).toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

function formatRelative(d: string) {
  const diff = Date.now() - new Date(d).getTime();
  const m = Math.floor(diff / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export function SignalsPage() {
  const navigate = useNavigate();
  const pag = usePagination(50);
  const [page, setPage] = useState(1);
  const [selectedSignal, setSelectedSignal] = useState<SignalEntry | null>(null);
  const [filterType, setFilterType] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterProcessed, setFilterProcessed] = useState('');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [payloadConditions, setPayloadConditions] = useState<PayloadCondition[]>([]);
  // Staged conditions (not yet submitted)
  const [stagedConditions, setStagedConditions] = useState<PayloadCondition[]>([]);
  const [showPayloadFilter, setShowPayloadFilter] = useState(false);

  function resetFilters() {
    setFilterType('');
    setFilterCustomer('');
    setFilterProcessed('');
    setFilterFrom('');
    setFilterTo('');
    setPayloadConditions([]);
    setStagedConditions([]);
    setPage(1);
  }

  function applyPayloadFilter() {
    const valid = stagedConditions.filter((c) => c.field.trim());
    setPayloadConditions(valid);
    setPage(1);
  }

  function addStagedCondition() {
    if (stagedConditions.length < 3) {
      setStagedConditions((prev) => [...prev, emptyCondition()]);
    }
  }

  function removeStagedCondition(i: number) {
    setStagedConditions((prev) => prev.filter((_, idx) => idx !== i));
  }

  function updateStagedCondition(i: number, patch: Partial<PayloadCondition>) {
    setStagedConditions((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  }

  const hasFilters = !!(
    filterType ||
    filterCustomer ||
    filterProcessed ||
    filterFrom ||
    filterTo ||
    payloadConditions.length > 0
  );

  const payloadFilterParam =
    payloadConditions.length > 0 ? JSON.stringify(payloadConditions) : undefined;

  const { data, isLoading } = useQuery<PagedResponse<SignalEntry>>({
    queryKey: [
      'signals',
      page,
      filterType,
      filterCustomer,
      filterProcessed,
      filterFrom,
      filterTo,
      payloadFilterParam,
    ],
    queryFn: () =>
      api
        .get('/api/v1/signals', {
          params: {
            page,
            limit: pag.limit,
            ...(filterType ? { eventType: filterType } : {}),
            ...(filterCustomer ? { customerId: filterCustomer } : {}),
            ...(filterProcessed !== '' ? { processed: filterProcessed } : {}),
            ...(filterFrom ? { from: filterFrom } : {}),
            ...(filterTo ? { to: filterTo } : {}),
            ...(payloadFilterParam ? { payloadFilter: payloadFilterParam } : {}),
          },
        })
        .then((r) => r.data),
    refetchInterval: 10_000,
    placeholderData: (prev) => prev,
  });

  const signals: SignalEntry[] =
    data?.items ?? (Array.isArray(data) ? (data as unknown as SignalEntry[]) : []);
  const total = data?.total ?? signals.length;
  const totalPages = pag.totalPages(total);

  // Distinct signal types from current page for dropdown
  const signalTypes = [...new Set(signals.map((e) => e.eventType))].sort();

  return (
    <div>
      <PageHeader
        title="Signal Explorer"
        description="Inbound signals — auto-refreshes every 10s"
        action={
          <span className="text-xs text-zinc-400 font-medium tabular-nums">
            {total.toLocaleString()} signals
          </span>
        }
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        {/* Signal type */}
        <div className="relative">
          <select
            value={filterType}
            onChange={(e) => {
              setFilterType(e.target.value);
              setPage(1);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 appearance-none min-w-[180px]"
          >
            <option value="">All signal types</option>
            {signalTypes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">
            ▾
          </span>
        </div>

        {/* Customer ID */}
        <div className="relative">
          <Search
            size={13}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none"
          />
          <input
            type="text"
            value={filterCustomer}
            onChange={(e) => {
              setFilterCustomer(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by customer ID…"
            className="h-9 pl-9 pr-8 bg-white border border-zinc-200 rounded-lg text-sm placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-zinc-900 w-52 font-mono"
          />
          {filterCustomer && (
            <button
              onClick={() => {
                setFilterCustomer('');
                setPage(1);
              }}
              className="absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
            >
              <X size={12} />
            </button>
          )}
        </div>

        {/* Processed filter */}
        <div className="relative">
          <select
            value={filterProcessed}
            onChange={(e) => {
              setFilterProcessed(e.target.value);
              setPage(1);
            }}
            className="h-9 pl-3 pr-8 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900 appearance-none min-w-[130px]"
          >
            <option value="">All statuses</option>
            <option value="true">Processed</option>
            <option value="false">Pending</option>
          </select>
          <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">
            ▾
          </span>
        </div>

        {/* Date range */}
        <input
          type="date"
          value={filterFrom}
          onChange={(e) => {
            setFilterFrom(e.target.value);
            setPage(1);
          }}
          className="h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900"
          placeholder="From"
          title="From date"
        />
        <input
          type="date"
          value={filterTo}
          onChange={(e) => {
            setFilterTo(e.target.value);
            setPage(1);
          }}
          className="h-9 px-3 bg-white border border-zinc-200 rounded-lg text-sm text-zinc-700 focus:outline-none focus:ring-2 focus:ring-zinc-900"
          placeholder="To"
          title="To date"
        />

        {/* Payload filter toggle */}
        <button
          onClick={() => {
            setShowPayloadFilter((v) => !v);
            if (!showPayloadFilter && stagedConditions.length === 0) {
              setStagedConditions([emptyCondition()]);
            }
          }}
          className={`h-9 px-3 text-xs rounded-lg border transition-colors ${
            payloadConditions.length > 0
              ? 'border-zinc-900 bg-zinc-900 text-white'
              : 'border-zinc-200 text-zinc-500 hover:text-zinc-800 hover:border-zinc-300'
          }`}
        >
          Payload filter {payloadConditions.length > 0 && `(${payloadConditions.length})`}
        </button>

        {hasFilters && (
          <button
            onClick={resetFilters}
            className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
          >
            Clear filters
          </button>
        )}
      </div>

      {/* Payload filter builder */}
      {showPayloadFilter && (
        <div className="mb-4 p-4 bg-zinc-50 border border-zinc-200 rounded-xl space-y-3">
          <p className="text-xs font-medium text-zinc-600">
            Payload Filter <span className="text-zinc-400 font-normal">(AND logic, max 3)</span>
          </p>
          <div className="space-y-2">
            {stagedConditions.map((cond, i) => (
              <div key={i} className="flex items-center gap-2">
                <input
                  type="text"
                  value={cond.field}
                  onChange={(e) => updateStagedCondition(i, { field: e.target.value })}
                  placeholder="field (e.g. articleId)"
                  className="flex-1 h-8 px-2.5 border border-zinc-200 rounded-lg text-xs font-mono bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                />
                <select
                  value={cond.operator}
                  onChange={(e) =>
                    updateStagedCondition(i, { operator: e.target.value as PayloadOperator })
                  }
                  className="h-8 px-2 border border-zinc-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                >
                  {OPERATORS.map((op) => (
                    <option key={op.value} value={op.value}>
                      {op.label}
                    </option>
                  ))}
                </select>
                {cond.operator !== 'exists' && (
                  <input
                    type="text"
                    value={cond.value}
                    onChange={(e) => updateStagedCondition(i, { value: e.target.value })}
                    placeholder="value"
                    className="flex-1 h-8 px-2.5 border border-zinc-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 focus:ring-zinc-900"
                  />
                )}
                <button
                  onClick={() => removeStagedCondition(i)}
                  className="text-zinc-300 hover:text-red-400 transition-colors"
                >
                  <X size={14} />
                </button>
              </div>
            ))}
          </div>
          <div className="flex items-center gap-2">
            {stagedConditions.length < 3 && (
              <button
                onClick={addStagedCondition}
                className="text-xs text-zinc-400 hover:text-zinc-700 transition-colors"
              >
                + Add condition
              </button>
            )}
            <button
              onClick={applyPayloadFilter}
              className="ml-auto h-8 px-4 text-xs bg-zinc-900 text-white rounded-lg hover:bg-zinc-700 transition-colors"
            >
              Search
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-zinc-100">
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Time
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Signal Type
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Customer ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Source
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                Status
              </th>
              <th className="px-4 py-3 w-8" />
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-50">
            {isLoading ? (
              <SkeletonRows count={8} cols={6} />
            ) : signals.length === 0 ? (
              <tr>
                <td colSpan={6}>
                  <EmptyState
                    icon={<Activity size={26} />}
                    title={hasFilters ? 'No signals match the filters' : 'No signals yet'}
                    description="Signals are ingested via POST /api/v1/signals and appear here in real-time."
                  />
                </td>
              </tr>
            ) : (
              signals.map((ev) => (
                <tr
                  key={ev.id}
                  onClick={() => setSelectedSignal(ev)}
                  className="hover:bg-zinc-50/70 cursor-pointer transition-colors group"
                >
                  <td className="px-4 py-2.5">
                    <p className="text-xs text-zinc-700 tabular-nums whitespace-nowrap">
                      {formatTs(ev.occurredAt)}
                    </p>
                    <p className="text-[10px] text-zinc-400">{formatRelative(ev.occurredAt)}</p>
                  </td>
                  <td className="px-4 py-2.5">
                    <code className="text-xs font-mono text-zinc-800 bg-zinc-50 px-1.5 py-0.5 rounded">
                      {ev.eventType}
                    </code>
                  </td>
                  <td className="px-4 py-2.5">
                    {ev.customerId ? (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/customers/${ev.customerId}`);
                        }}
                        className="text-[10px] font-mono text-sky-600 hover:text-sky-800 hover:underline"
                      >
                        {ev.customerId.slice(0, 8)}…
                      </button>
                    ) : (
                      <span className="text-zinc-300 text-xs">—</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-xs text-zinc-500">{ev.source}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={ev.processedAt ? 'success' : 'warning'} dot>
                        {ev.processedAt ? 'processed' : 'pending'}
                      </Badge>
                      {ev.schemaWarnings && ev.schemaWarnings.length > 0 && (
                        <span
                          title={ev.schemaWarnings.join('\n')}
                          className="text-amber-400 cursor-help text-xs"
                        >
                          ⚠
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-zinc-300 group-hover:text-zinc-500 transition-colors text-xs">
                    ›
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {totalPages > 1 && (
          <div className="border-t border-zinc-100 px-4">
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Payload modal */}
      <Modal
        open={!!selectedSignal}
        onClose={() => setSelectedSignal(null)}
        title={selectedSignal?.eventType ?? ''}
        description={selectedSignal ? formatTs(selectedSignal.occurredAt) : undefined}
        size="lg"
      >
        {selectedSignal && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div className="space-y-0.5">
                <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-medium">
                  Customer ID
                </p>
                {selectedSignal.customerId ? (
                  <button
                    onClick={() => {
                      setSelectedSignal(null);
                      navigate(`/customers/${selectedSignal.customerId}`);
                    }}
                    className="font-mono text-sky-600 hover:underline"
                  >
                    {selectedSignal.customerId}
                  </button>
                ) : (
                  <code className="font-mono text-zinc-400">—</code>
                )}
              </div>
              <div className="space-y-0.5">
                <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-medium">
                  Source
                </p>
                <code className="font-mono text-zinc-700">{selectedSignal.source}</code>
              </div>
              <div className="space-y-0.5">
                <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-medium">
                  Status
                </p>
                <Badge variant={selectedSignal.processedAt ? 'success' : 'warning'} dot>
                  {selectedSignal.processedAt ? 'processed' : 'pending'}
                </Badge>
              </div>
              <div className="space-y-0.5">
                <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-medium">
                  Signal ID
                </p>
                <code className="font-mono text-zinc-400 text-[10px]">{selectedSignal.id}</code>
              </div>
              {selectedSignal.dedupeKey && (
                <div className="space-y-0.5 col-span-2">
                  <p className="text-zinc-400 uppercase tracking-wide text-[10px] font-medium">
                    Dedupe Key
                  </p>
                  <code className="font-mono text-zinc-500 text-[10px]">
                    {selectedSignal.dedupeKey}
                  </code>
                </div>
              )}
            </div>
            {selectedSignal.schemaWarnings && selectedSignal.schemaWarnings.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-[10px] font-medium text-amber-600 uppercase tracking-wide mb-1.5">
                  Schema Warnings
                </p>
                <ul className="space-y-0.5">
                  {selectedSignal.schemaWarnings.map((w, i) => (
                    <li key={i} className="text-xs text-amber-700 font-mono">
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <p className="text-[10px] font-medium text-zinc-400 uppercase tracking-wide mb-2">
                Payload
              </p>
              <pre className="bg-zinc-950 text-zinc-100 text-xs font-mono rounded-xl p-4 overflow-auto max-h-72 leading-relaxed">
                {JSON.stringify(selectedSignal.payloadJson ?? {}, null, 2)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
